import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { ParallaxMedia } from '../shared/ParallaxMedia';
import { founder, salon } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { useCatalog } from '../../contexts/CatalogContext';
import type { ArticleCategory } from '../../core/types/content';
import { plural } from '../../core/format/text';
import { fill } from '../../utils/copy';
import { btnPrimaryLg, chipCls } from '../../ui/styles';
import { heroTitle } from '../../ui/sections';
import { pageActions } from '../../data/pageActions';
import { articleImage } from '../../data/placeholderImages';
import { MentionedProducts } from './MentionedProducts';
import { JournalPodcast } from './JournalPodcast';
import { articlePath, articlesIn, categoriesOf } from './journalRules';

/**
 * Het journal als tijdschrift: kop, het nieuwste artikel groot met beeld en genoemde producten,
 * de rest in een raster (elke kaart met beeld), en de podcast op blush met de Instagram-regel.
 * Geen klei op deze pagina: de footer is klei.
 */
export function Journal() {
  const pages = usePages();
  const {
    articles,
    productsByIds
  } = useCatalog();
  const copy = pages.journal;
  const categories = categoriesOf(articles);
  // De categorie staat in het adres (?categorie=…): terug uit een artikel staat dezelfde keuze er nog.
  const [params, setParams] = useSearchParams();
  const asked = params.get('categorie');
  const category: ArticleCategory | null = categories.find(c => c === asked) ?? null;
  const setCategory = (c: ArticleCategory | null) => {
    const next = new URLSearchParams(params);
    if (c) next.set('categorie', c);else next.delete('categorie');
    setParams(next, {
      replace: true,
      preventScrollReset: true
    });
  };
  const linkState = {
    fromJournal: `/journal${params.toString() ? `?${params.toString()}` : ''}`
  };
  const [featured, ...rest] = articlesIn(articles, category);
  const featuredProducts = productsByIds(featured?.productIds ?? []);
  return <>
      <section className="bg-sand" data-id="element-7863" data-mp="3auoecdrqbqm6">
        <div className="mx-auto grid max-w-content gap-10 px-6 pb-10 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-14 lg:pt-20" data-id="element-7864" data-mp="3fvcb7g2n31ob">
          <div className="lg:col-span-8" data-id="element-7865" data-mp="3kuow3v9y9yo9">
            {copy.hero.eyebrow && <p className="text-[14px] text-clay" data-id="element-7866" data-mp="2n6oodg28s48n">{fill(copy.hero.eyebrow)}</p>}
            <h1 className={`mt-4 max-w-3xl lg:mt-5 ${heroTitle}`} data-id="element-7867" data-mp="2n4lo837dkuq7">
              <HeadingText heading={copy.hero.heading} data-id="element-7868" data-mp="2s6jevii3urt6" />
            </h1>
          </div>
          <div className="lg:col-span-4 lg:self-end" data-id="element-7869" data-mp="2x3taxh0021pz">
            <div className="flex items-center gap-4 border-t border-ink/15 pt-6" data-id="element-7870" data-mp="2x31habpyqe2c">
              {/* Decoratief: haar naam staat ernaast. */}
              <img src={founder.portrait} alt="" width={56} height={56} className="h-14 w-14 shrink-0 rounded-full object-cover object-top" data-id="element-7871" data-mp="24f5ld987wq6m" />
              <p className="text-[14px] leading-snug text-muted" data-id="element-7872" data-mp="29d70xk6fq7dc">
                {fill(copy.hero.byline)} <span className="text-ink" data-id="element-7873" data-mp="21uzeasob4jlu">{founder.name}</span>
                <br data-id="element-7874" data-mp="1wxpj0we8ldq3" />
                {founder.role} · {salon.city}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Artikelen: eerst, want hier komen lezers via Google binnen */}
      <section className="bg-sand pb-20 sm:pb-24 lg:pb-32" aria-labelledby="journal-articles" data-id="element-7875" data-mp="1mxyncrdxwsof">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7876" data-mp="1iyg37xty0lmn">
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-ink/15 pt-6" data-id="element-7877" data-mp="1e5mwx5itt58g">
            <h2 id="journal-articles" className="text-[14px] text-ink" data-id="element-7878" data-mp="196adkur5ygam">
              {fill(copy.articlesHeading)}
            </h2>
            <div role="group" aria-label="Kies een categorie" className="-mx-6 flex min-w-0 max-w-[calc(100%+3rem)] gap-2 overflow-x-auto px-6 [scrollbar-width:none] sm:mx-0 sm:max-w-full sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden" data-id="element-7879" data-mp="149jzue2tzwsd">
              <button type="button" aria-pressed={category === null} onClick={() => setCategory(null)} className={`${chipCls(category === null)} shrink-0`} data-id="element-7880" data-mp="1jyi20oe8jij1">
                Alles
              </button>
              {categories.map(c => <button key={c} type="button" aria-pressed={category === c} onClick={() => setCategory(c)} className={`${chipCls(category === c)} shrink-0`} data-id="element-7881" data-mp="1f78bzmoryd6m">
                  {c}
                </button>)}
            </div>
          </div>

          {featured ? <article className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-16" aria-labelledby="featured-title" data-id="element-7882" data-mp="164h1bp3q3lg1">
              <Link to={articlePath(featured)} state={linkState} className="block lg:col-span-7" tabIndex={-1} aria-hidden="true" data-id="element-7883" data-mp="w8dx8dpb9cqg">
                {/* Sfeerbeeld: de titel staat ernaast. Nooit leeg (articleImage).
                    Het ene moment van deze pagina: het beeld beweegt licht binnen zijn kader. */}
                <ParallaxMedia media={{
              image: articleImage(featured),
              alt: ''
            }} priority sizes="(min-width: 1024px) 55vw, 100vw" className="aspect-[4/3] w-full" data-id="element-7884" data-mp="eys3otcwjmvs" />
              </Link>
              <div className="lg:col-span-5 lg:self-center" data-id="element-7885" data-mp="oouviq8aeusj">
                <p className="text-[13px] text-clay" data-id="element-7886" data-mp="oqfpadms29pc">
                  {featured.category} · {featured.readTime} · {featured.date}
                </p>
                <h3 id="featured-title" className="mt-3 type-sub text-ink" data-id="element-7887" data-mp="ymsmhlhj75ou">
                  <Link to={articlePath(featured)} state={linkState} className="transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7888" data-mp="yovkaroxe344">
                    {featured.title}
                  </Link>
                </h3>
                <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-7889" data-mp="12ovzwfnrgkcr">{featured.excerpt}</p>
                {/* De hoofdknop van het journal: het nieuwste artikel. */}
                <Link to={articlePath(featured)} state={linkState} className={`group mt-7 ${btnPrimaryLg}`} data-id="element-7890" data-mp="39gx0w4stdz9w">
                  {pageActions.readLatest}
                  <span className="sr-only" data-id="element-7891" data-mp="18dw5ur0x5tku">: {featured.title}</span>
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7892" data-mp="1dbp8xb6xe7gw" />
                </Link>

                {featuredProducts.length > 0 && <div className="mt-9 border-t border-ink/15 pt-6" data-id="element-7893" data-mp="1mdf1ikcjncg9">
                    <p className="text-[14px] text-ink" data-id="element-7894" data-mp="1rc8du9g3gxeu">Genoemd in dit artikel</p>
                    <MentionedProducts products={featuredProducts} data-id="element-7895" data-mp="1wajr6u5llq1t" />
                  </div>}
              </div>
            </article> : <p className="mt-10 text-[17px] text-muted" data-id="element-7896" data-mp="212bgz2gxjrqw">{fill(copy.emptyCategory)}</p>}

          {rest.length > 0 && <ul className="mt-20 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-id="element-7897" data-mp="2a2htal0w6loh">
              {rest.map(a => <li key={a.slug} className="h-full" data-id="element-7898" data-mp="2f0jd58656p10">
                  <Link to={articlePath(a)} state={linkState} className="group flex h-full flex-col" data-id="element-7899" data-mp="2ez84ov1r4t3k">
                    {/* Elke kaart een beeld in dezelfde maat, zodat titels in de rij gelijk staan. */}
                    <MediaView media={{
                image: articleImage(a),
                alt: ''
              }} sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw" className="aspect-[3/2] w-full" data-id="element-7900" data-mp="3umlj3sx02vy6" />
                    <p className="mt-5 text-[13px] text-clay" data-id="element-7901" data-mp="3llf11fepdd2t">{a.category}</p>
                    <h3 className="mt-2 type-title text-ink transition-colors duration-150 ease-soft group-hover:text-clay" data-id="element-7902" data-mp="m35mkbq261xf">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-7903" data-mp="dbc0k9rrfz0g">{a.excerpt}</p>
                    <p className="mt-auto pt-4 text-[13px] text-muted" data-id="element-7904" data-mp="8dk8nfr48cw9">
                      {a.date} · {a.readTime}
                      {a.productIds && a.productIds.length > 0 && <span className="text-clay" data-id="element-7905" data-mp="15u9b9bnw3j6p"> · {plural(a.productIds.length, 'product', 'producten')} uit de shop</span>}
                    </p>
                  </Link>
                </li>)}
            </ul>}
        </div>
      </section>

      <JournalPodcast data-id="element-7906" data-mp="e5mz9iwj7drj" />
    </>;
}